/**
 * THE AI TRANSPARENCY dictionary (F4). English origin, pt-BR translation —
 * same seed and shape as `catalog.ts`.
 *
 * The ONE sentence of pricing lives here — but its NUMBERS do not. `{fast}`,
 * `{smart}` and `{premium}` come from `GET /v3/credits` → `prices.turn.<area>`, which
 * is the same `CreditPrices` the charge debits (back: `CREDITS_PER_TURN_*` in
 * the `.env`, the one source). Everything is an "answer" at its tier's price —
 * no thresholds, no reclassification (the owner vetoed both). Usage screens speak in
 * answers and credits, without a monetary conversion.
 *
 * ── POR QUE OS NÚMEROS SAÍRAM DA FRASE (27/09/2026) ───────────────────────
 *
 * A frase dizia "premium 16 — leves 0 · médias 2 · pesadas 10" escrito à mão
 * nos sete idiomas, enquanto a cobrança lia premium 8 do `.env` e a tabela de
 * ferramentas já não tinha nada a 10. Três fontes, três verdades. Agora a frase
 * só tem lugares, e o servidor põe os números.
 *
 * ── AS FERRAMENTAS: UM TETO, NÃO TRÊS FAIXAS ──────────────────────────────
 *
 * `tool_credit_cost` (0145) é preço POR FERRAMENTA — "faixas" nunca existiram
 * no dado, só no comentário da 0181. A tela diz o que é verdade e cabe numa
 * frase de venda: algumas ações custam à parte, até `{max}` (a mais cara da
 * tabela, `prices.tools.max`). Com `max` a zero a segunda frase não aparece.
 * Nenhum modelo é nomeado aqui, e nunca será: o cliente compra respostas.
 *
 * ── PELO CUSTO NÃO HÁ NÚMERO FIXO (06/10/2026) ────────────────────────────
 *
 * Com o coeficiente de uma área ligado (0280), a resposta custa o trabalho
 * que deu. A tela diz isso por área (`costPrice`), com a resposta típica DO
 * WORKSPACE quando há histórico (`prices.turn.<área>.typical`) e sem número
 * nenhum quando não há (`costPriceNew`). `pricePhrase` só fala quando as duas
 * áreas cobram pelo mesmo preço por nível — a tela de antes.
 */
export const aiMessages = {
	en: {
		pricePhrase: "Each AI answer spends credits: fast {fast} · smart {smart} · premium {premium}.",
		toolPhrase: "Some of Ben's actions cost extra: up to {max} credits each.",
		pricesLead: "Each AI answer spends credits:",
		areaAttendant: "Customer service",
		areaBen: "Ben",
		tierPrice: "fast {fast} · smart {smart} · premium {premium}",
		costPrice: "by the work each answer took · typically ≈ {n} credits",
		costPriceNew: "by the work each answer took",
		thisMonth: "this cycle: {answers} answer | this cycle: {answers} answers",
		spentCredits: "{n} credits spent this cycle",
		ceiling: {
			title: "Spending ceiling",
			lead: "Warn me at X, stop at Y — in credits spent within the cycle. The warning rings the bell once; the stop refuses any answer that would cross it until you raise it. An answer's price never goes past the cap; paid Ben actions may add a few credits.",
			warnLabel: "Warn at",
			stopLabel: "Stop at",
			none: "no limit",
			save: "Save ceiling",
			saving: "Saving…",
			saved: "Ceiling saved.",
			failed: "Could not save the ceiling. Nothing changed.",
			warnAboveStop: "The warning line must not be above the stop line.",
			blocked:
				"The stop line was reached this cycle: the AI is paused. Raise or clear the ceiling to continue.",
			meter: "{spent} of {stop} credits used",
		},
		trial: {
			daysLeft: "Trial: {n} day left | Trial: {n} days left",
			lastDay: "Trial: last day",
			ended: "Trial ended",
			hint: "Choose a plan to keep the AI answering. Nothing is deleted.",
			cta: "Choose a plan",
		},
	},
	"pt-BR": {
		pricePhrase:
			"Cada resposta da IA desconta créditos: resposta rápida {fast} · inteligente {smart} · premium {premium}.",
		toolPhrase: "Algumas ações do Ben custam à parte: até {max} créditos cada.",
		pricesLead: "Cada resposta da IA desconta créditos:",
		areaAttendant: "Atendimento",
		areaBen: "Ben",
		tierPrice: "rápida {fast} · inteligente {smart} · premium {premium}",
		costPrice: "conforme o trabalho que cada resposta deu · em geral ≈ {n} créditos",
		costPriceNew: "conforme o trabalho que cada resposta deu",
		thisMonth: "neste ciclo: {answers} resposta | neste ciclo: {answers} respostas",
		spentCredits: "{n} créditos usados neste ciclo",
		ceiling: {
			title: "Teto de gastos",
			lead: "Avise em X, pare em Y — em créditos usados dentro do ciclo. O aviso toca o sino uma vez; a parada recusa a resposta que passaria da linha até você aumentar. O preço da resposta nunca passa do teto; ações pagas do Ben podem somar alguns créditos.",
			warnLabel: "Avisar em",
			stopLabel: "Parar em",
			none: "sem limite",
			save: "Salvar teto",
			saving: "Salvando…",
			saved: "Teto salvo.",
			failed: "Não deu para salvar o teto. Nada mudou.",
			warnAboveStop: "A linha de aviso não pode ficar acima da de parada.",
			blocked:
				"A linha de parada foi atingida neste ciclo: a IA está pausada. Aumente ou limpe o teto para continuar.",
			meter: "{spent} de {stop} créditos usados",
		},
		trial: {
			daysLeft: "Teste: falta {n} dia | Teste: faltam {n} dias",
			lastDay: "Teste: último dia",
			ended: "O teste terminou",
			hint: "Escolha um plano para a IA continuar respondendo. Nada é apagado.",
			cta: "Escolher um plano",
		},
	},
	el: {
		pricePhrase:
			"Κάθε απάντηση της AI ξοδεύει πιστώσεις: γρήγορη {fast} · έξυπνη {smart} · premium {premium}.",
		toolPhrase: "Ορισμένες ενέργειες του Ben χρεώνονται χωριστά: έως {max} πιστώσεις η καθεμία.",
		pricesLead: "Κάθε απάντηση της AI ξοδεύει πιστώσεις:",
		areaAttendant: "Εξυπηρέτηση",
		areaBen: "Ben",
		tierPrice: "γρήγορη {fast} · έξυπνη {smart} · premium {premium}",
		costPrice: "ανάλογα με τη δουλειά κάθε απάντησης · συνήθως ≈ {n} πιστώσεις",
		costPriceNew: "ανάλογα με τη δουλειά κάθε απάντησης",
		thisMonth: "αυτόν τον κύκλο: {answers} απάντηση | αυτόν τον κύκλο: {answers} απαντήσεις",
		spentCredits: "{n} πιστώσεις ξοδεύτηκαν αυτόν τον κύκλο",
		ceiling: {
			title: "Ανώτατο όριο δαπάνης",
			lead: "Ειδοποίηση στο X, διακοπή στο Y — σε πιστώσεις που ξοδεύτηκαν μέσα στον κύκλο. Η ειδοποίηση χτυπά το καμπανάκι μία φορά, ενώ η διακοπή αρνείται κάθε απάντηση που θα το ξεπερνούσε μέχρι να το ανεβάσεις. Η τιμή μιας απάντησης δεν ξεπερνά ποτέ το όριο· οι επί πληρωμή ενέργειες του Ben μπορεί να προσθέσουν λίγες πιστώσεις.",
			warnLabel: "Ειδοποίηση στις",
			stopLabel: "Διακοπή στις",
			none: "χωρίς όριο",
			save: "Αποθήκευση ορίου",
			saving: "Αποθήκευση…",
			saved: "Το όριο αποθηκεύτηκε.",
			failed: "Δεν ήταν δυνατή η αποθήκευση του ορίου. Τίποτα δεν άλλαξε.",
			warnAboveStop: "Η γραμμή ειδοποίησης δεν πρέπει να είναι πάνω από τη γραμμή διακοπής.",
			blocked:
				"Η γραμμή διακοπής συμπληρώθηκε αυτόν τον κύκλο: η AI είναι σε παύση. Ανέβασε ή καθάρισε το όριο για να συνεχίσεις.",
			meter: "{spent} από {stop} πιστώσεις σε χρήση",
		},
		trial: {
			daysLeft: "Δοκιμή: απομένει {n} ημέρα | Δοκιμή: απομένουν {n} ημέρες",
			lastDay: "Δοκιμή: τελευταία ημέρα",
			ended: "Η δοκιμή τελείωσε",
			hint: "Διάλεξε πλάνο για να συνεχίσει η AI να απαντά. Τίποτα δεν διαγράφεται.",
			cta: "Επιλογή πλάνου",
		},
	},
	es: {
		pricePhrase:
			"Cada respuesta de la IA gasta créditos: rápida {fast} · inteligente {smart} · premium {premium}.",
		toolPhrase: "Algunas acciones de Ben cuestan aparte: hasta {max} créditos cada una.",
		pricesLead: "Cada respuesta de la IA gasta créditos:",
		areaAttendant: "Atención",
		areaBen: "Ben",
		tierPrice: "rápida {fast} · inteligente {smart} · premium {premium}",
		costPrice: "según el trabajo de cada respuesta · en general ≈ {n} créditos",
		costPriceNew: "según el trabajo de cada respuesta",
		thisMonth: "en este ciclo: {answers} respuesta | en este ciclo: {answers} respuestas",
		spentCredits: "{n} créditos gastados en este ciclo",
		ceiling: {
			title: "Límite de gasto",
			lead: "Avísame en X, para en Y — en créditos gastados dentro del ciclo. El aviso toca la campana una vez; la parada rechaza la respuesta que cruzaría la línea hasta que lo subas. El precio de la respuesta nunca pasa del tope; las acciones de pago de Ben pueden sumar algunos créditos.",
			warnLabel: "Avisar en",
			stopLabel: "Parar en",
			none: "sin límite",
			save: "Guardar límite",
			saving: "Guardando…",
			saved: "Límite guardado.",
			failed: "No se pudo guardar el límite. No ha cambiado nada.",
			warnAboveStop: "La línea de aviso no puede estar por encima de la de parada.",
			blocked:
				"Se alcanzó la línea de parada en este ciclo: la IA está en pausa. Sube o quita el límite para continuar.",
			meter: "{spent} de {stop} créditos usados",
		},
		trial: {
			daysLeft: "Prueba: queda {n} día | Prueba: quedan {n} días",
			lastDay: "Prueba: último día",
			ended: "La prueba ha terminado",
			hint: "Elige un plan para que la IA siga respondiendo. No se borra nada.",
			cta: "Elegir un plan",
		},
	},
	fr: {
		pricePhrase:
			"Chaque réponse de l'IA consomme des crédits : rapide {fast} · intelligente {smart} · premium {premium}.",
		toolPhrase: "Certaines actions de Ben sont facturées à part : jusqu'à {max} crédits chacune.",
		pricesLead: "Chaque réponse de l'IA consomme des crédits :",
		areaAttendant: "Service client",
		areaBen: "Ben",
		tierPrice: "rapide {fast} · intelligente {smart} · premium {premium}",
		costPrice: "selon le travail de chaque réponse · en général ≈ {n} crédits",
		costPriceNew: "selon le travail de chaque réponse",
		thisMonth: "sur ce cycle : {answers} réponse | sur ce cycle : {answers} réponses",
		spentCredits: "{n} crédits consommés sur ce cycle",
		ceiling: {
			title: "Plafond de dépense",
			lead: "Prévenez-moi à X, arrêtez à Y — en crédits consommés dans le cycle. L'alerte sonne la cloche une fois ; l'arrêt refuse toute réponse qui le dépasserait tant que vous ne le relevez pas. Le prix d'une réponse ne dépasse jamais le plafond ; les actions payantes de Ben peuvent ajouter quelques crédits.",
			warnLabel: "Alerte à",
			stopLabel: "Arrêt à",
			none: "sans limite",
			save: "Enregistrer le plafond",
			saving: "Enregistrement…",
			saved: "Plafond enregistré.",
			failed: "Impossible d'enregistrer le plafond. Rien n'a changé.",
			warnAboveStop: "La ligne d'alerte ne peut pas dépasser la ligne d'arrêt.",
			blocked:
				"La ligne d'arrêt a été atteinte sur ce cycle : l'IA est en pause. Relevez ou supprimez le plafond pour continuer.",
			meter: "{spent} crédits sur {stop} utilisés",
		},
		trial: {
			daysLeft: "Essai : {n} jour restant | Essai : {n} jours restants",
			lastDay: "Essai : dernier jour",
			ended: "L'essai est terminé",
			hint: "Choisissez un forfait pour que l'IA continue de répondre. Rien n'est supprimé.",
			cta: "Choisir un forfait",
		},
	},
	it: {
		pricePhrase:
			"Ogni risposta dell'IA consuma crediti: veloce {fast} · intelligente {smart} · premium {premium}.",
		toolPhrase: "Alcune azioni di Ben costano a parte: fino a {max} crediti ciascuna.",
		pricesLead: "Ogni risposta dell'IA consuma crediti:",
		areaAttendant: "Assistenza",
		areaBen: "Ben",
		tierPrice: "veloce {fast} · intelligente {smart} · premium {premium}",
		costPrice: "in base al lavoro di ogni risposta · di solito ≈ {n} crediti",
		costPriceNew: "in base al lavoro di ogni risposta",
		thisMonth: "in questo ciclo: {answers} risposta | in questo ciclo: {answers} risposte",
		spentCredits: "{n} crediti consumati in questo ciclo",
		ceiling: {
			title: "Tetto di spesa",
			lead: "Avvisami a X, fermati a Y — in crediti consumati dentro il ciclo. L'avviso suona la campanella una volta; il blocco rifiuta la risposta che lo supererebbe finché non lo alzi. Il prezzo di una risposta non supera mai il tetto; le azioni a pagamento di Ben possono aggiungere qualche credito.",
			warnLabel: "Avvisa a",
			stopLabel: "Ferma a",
			none: "nessun limite",
			save: "Salva il tetto",
			saving: "Salvataggio…",
			saved: "Tetto salvato.",
			failed: "Non è stato possibile salvare il tetto. Non è cambiato nulla.",
			warnAboveStop: "La soglia di avviso non può stare sopra quella di blocco.",
			blocked:
				"La soglia di blocco è stata raggiunta in questo ciclo: l'IA è in pausa. Alza o azzera il tetto per continuare.",
			meter: "{spent} di {stop} crediti usati",
		},
		trial: {
			daysLeft: "Prova: manca {n} giorno | Prova: mancano {n} giorni",
			lastDay: "Prova: ultimo giorno",
			ended: "La prova è finita",
			hint: "Scegli un piano per far continuare a rispondere l'IA. Non viene cancellato nulla.",
			cta: "Scegli un piano",
		},
	},
	de: {
		pricePhrase:
			"Jede KI-Antwort kostet Credits: schnell {fast} · schlau {smart} · premium {premium}.",
		toolPhrase: "Manche Aktionen von Ben kosten extra: bis zu {max} Credits pro Aktion.",
		pricesLead: "Jede KI-Antwort kostet Credits:",
		areaAttendant: "Kundenservice",
		areaBen: "Ben",
		tierPrice: "schnell {fast} · schlau {smart} · premium {premium}",
		costPrice: "nach Aufwand der Antwort · meist ≈ {n} Credits",
		costPriceNew: "nach Aufwand der Antwort",
		thisMonth: "in diesem Zyklus: {answers} Antwort | in diesem Zyklus: {answers} Antworten",
		spentCredits: "{n} Credits in diesem Zyklus verbraucht",
		ceiling: {
			title: "Ausgabengrenze",
			lead: "Bei X warnen, bei Y stoppen — in Credits, die im Zyklus verbraucht werden. Die Warnung läutet einmal die Glocke; der Stopp verweigert jede Antwort, die die Grenze überschreiten würde, bis Sie sie anheben. Der Preis einer Antwort geht nie über die Grenze; kostenpflichtige Aktionen von Ben können ein paar Credits hinzufügen.",
			warnLabel: "Warnen bei",
			stopLabel: "Stoppen bei",
			none: "kein Limit",
			save: "Grenze speichern",
			saving: "Wird gespeichert…",
			saved: "Grenze gespeichert.",
			failed: "Die Grenze konnte nicht gespeichert werden. Es hat sich nichts geändert.",
			warnAboveStop: "Die Warnschwelle darf nicht über der Stoppschwelle liegen.",
			blocked:
				"Die Stoppschwelle ist in diesem Zyklus erreicht: die KI pausiert. Heben Sie die Grenze an oder löschen Sie sie, um fortzufahren.",
			meter: "{spent} von {stop} Credits verbraucht",
		},
		trial: {
			daysLeft: "Testphase: noch {n} Tag | Testphase: noch {n} Tage",
			lastDay: "Testphase: letzter Tag",
			ended: "Die Testphase ist beendet",
			hint: "Wählen Sie einen Tarif, damit die KI weiter antwortet. Es wird nichts gelöscht.",
			cta: "Tarif wählen",
		},
	},
} as const;
